import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Paper, Button } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AllocationModel, DeliveryModel, Unit } from '../../../../Generated/Commodity-Management-Api';
import { StoneXGrid } from '../../../StoneX';
import { convertVolume } from '../../../../Helpers';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import './DeliveriesData.css';

interface DeliveriesDataProps {
    allocations: AllocationModel[];
    displayUnit?: Unit;
}

interface DeliveryRow extends DeliveryModel {
    allocationName: string;
    originalQuantity: number;
    sourceUnit: Unit;
    bushelVolume: number | undefined;
}

// Map baseUnitofMeasure string to Unit enum
export const mapBaseUnitToUnit = (baseUnit: string | undefined | null): Unit => {
    if (!baseUnit) return Unit.Kg;
    
    const normalizedUnit = baseUnit.toUpperCase().trim();
    
    switch (normalizedUnit) {
        case 'KG':
        case 'KGS':
        case 'KILOGRAM':
        case 'KILOGRAMS':
            return Unit.Kg;
        case 'MT':
        case 'METRIC TON':
        case 'METRIC TONS':
            return Unit.Tonne;
        case 'LB':
        case 'LBS':
        case 'POUND':
        case 'POUNDS':
            return Unit.Lb;
        case 'BU':
        case 'BUSHEL':
        case 'BUSHELS':
            return Unit.Bu;
        case 'CWT':
        case 'HUNDREDWEIGHT':
            return Unit.Cwt;
        case 'ST':
        case 'SHORT TON':
        case 'SHORT TONS':
            return Unit.Ton;
        default:
            return Unit.Kg; // Default to KG if unknown
    }
};

const DeliveriesData: React.FC<DeliveriesDataProps> = ({ allocations, displayUnit }) => {
    const gridRef = useRef<any>(null);
    const [selectedAllocationId, setSelectedAllocationId] = useState<number | 'all'>('all');
    const [filteredQuantitySum, setFilteredQuantitySum] = useState<number>(0);
    const [filteredAmountSum, setFilteredAmountSum] = useState<number>(0);
    const [filteredRowCount, setFilteredRowCount] = useState<number>(0);
    const [totalRowCount, setTotalRowCount] = useState<number>(0);

    // Helper function to convert volume
    const getConvertedValue = useCallback((
        value: number | undefined | null, 
        fromUnit: Unit | undefined | null, 
        toUnit: Unit | undefined | null,
        bushelVolume: number | undefined | null
    ): number => {
        if (value == null) return 0;
        if (!fromUnit || !toUnit) return value;
        if (fromUnit === toUnit) return value;
        return convertVolume(value, fromUnit, toUnit, bushelVolume ?? undefined);
    }, []);

    // Get all deliveries based on selected allocation
    const deliveries = useMemo((): DeliveryRow[] => {
        const getDeliveriesFromAllocation = (alloc: AllocationModel): DeliveryRow[] => {
            // Get the bushel volume from the allocation's base budget for bushel conversions
            const baseBudget = alloc.budgets?.find(b => b.budgetRoundModel?.budgetRoundName === "Base");
            const bushelVolume = baseBudget?.budgetPricingStrategy?.bushelVolume;

            return (alloc.deliveries ?? []).map(d => {
                // Use the delivery's baseUnitofMeasure as the source unit
                const sourceUnit = mapBaseUnitToUnit(d.baseUnitofMeasure);
                
                return {
                    ...d,
                    allocationName: alloc.counterparty.counterpartyName,
                    originalQuantity: d.quantity ?? 0,
                    sourceUnit: sourceUnit,
                    bushelVolume: bushelVolume ?? undefined
                };
            });
        };

        if (selectedAllocationId === 'all') {
            return allocations.flatMap(alloc => getDeliveriesFromAllocation(alloc));
        }

        const selectedAllocation = allocations.find(a => a.allocationGroupId === selectedAllocationId);
        if (!selectedAllocation) return [];

        return getDeliveriesFromAllocation(selectedAllocation);
    }, [allocations, selectedAllocationId]);

    // Get effective display unit - use displayUnit prop or fall back to first allocation's unit
    const effectiveDisplayUnit = useMemo(() => {
        if (displayUnit) return displayUnit;
        if (allocations.length > 0) {
            const baseBudget = allocations[0].budgets?.find(b => b.budgetRoundModel?.budgetRoundName === "Base");
            return allocations[0].unit ?? baseBudget?.unit;
        }
        return undefined;
    }, [displayUnit, allocations]);

    // Apply unit conversion to deliveries
    const convertedDeliveries = useMemo((): DeliveryRow[] => {
        if (!effectiveDisplayUnit) return deliveries;

        return deliveries.map(d => ({
            ...d,
            quantity: getConvertedValue(d.originalQuantity, d.sourceUnit, effectiveDisplayUnit, d.bushelVolume)
        }));
    }, [deliveries, effectiveDisplayUnit, getConvertedValue]);

    // Calculate sums
    const calculateSums = useCallback((rows: DeliveryRow[]) => {
        const quantitySum = rows.reduce((acc, row) => acc + (row.quantity ?? 0), 0);
        const amountSum = rows.reduce((acc, row) => acc + (row.amountInLocation ?? 0), 0);
        setFilteredQuantitySum(quantitySum);
        setFilteredAmountSum(amountSum);
        setFilteredRowCount(rows.length);
    }, []);

    // Update totals when data changes
    useEffect(() => {
        setTotalRowCount(convertedDeliveries.length);
        calculateSums(convertedDeliveries);
    }, [convertedDeliveries, calculateSums]);

    const handleAllocationChange = (event: SelectChangeEvent<number | 'all'>) => {
        const value = event.target.value;
        setSelectedAllocationId(value === 'all' ? 'all' : Number(value));
    };

    const handleFilterChanged = useCallback(() => {
        const api = gridRef.current?.api;
        if (!api) return;

        const filteredRows: DeliveryRow[] = [];
        api.forEachNodeAfterFilter((node: any) => {
            if (node.data) {
                filteredRows.push(node.data);
            }
        });
        calculateSums(filteredRows);
    }, [calculateSums]);

    // Date filter params for AG Grid
    const dateFilterParams = {
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date | null) => {
            if (!cellValue) return -1;
            const cellDate = dayjs(cellValue).startOf('day').toDate();
            if (cellDate < filterLocalDateAtMidnight) return -1;
            if (cellDate > filterLocalDateAtMidnight) return 1;
            return 0;
        },
        browserDatePicker: true,
    };

    // Default column definition with header styling
    const defaultColDef: ColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        headerClass: 'ag-header-cell-left',
        suppressHeaderMenuButton: false,
    }), []);

    // Column definitions for AG Grid
    const columns: ColDef<DeliveryRow>[] = useMemo(() => [
        { 
            field: 'allocationName', 
            headerName: 'Supplier', 
            filter: 'agSetColumnFilter',
            minWidth: 150
        },
        { 
            field: 'postingDate', 
            headerName: 'Posting Date', 
            filter: 'agDateColumnFilter',
            filterParams: dateFilterParams,
            valueGetter: (params) => params.data?.postingDate ? new Date(params.data.postingDate) : null,
            valueFormatter: (params) => params.value ? dayjs(params.value).format('MM-DD-YYYY') : '',
            minWidth: 120
        },
        { 
            field: 'documentDate', 
            headerName: 'Document Date', 
            filter: 'agDateColumnFilter',
            filterParams: dateFilterParams,
            valueGetter: (params) => params.data?.documentDate ? new Date(params.data.documentDate) : null,
            valueFormatter: (params) => params.value ? dayjs(params.value).format('MM-DD-YYYY') : '',
            minWidth: 120
        },
        { 
            field: 'plant', 
            headerName: 'Plant', 
            filter: 'agSetColumnFilter',
            minWidth: 80
        },
        { 
            field: 'vendor', 
            headerName: 'Vendor', 
            filter: 'agSetColumnFilter',
            minWidth: 100
        },
        { 
            field: 'material', 
            headerName: 'Material', 
            filter: 'agSetColumnFilter',
            minWidth: 100
        },
        { 
            field: 'materialDescription', 
            headerName: 'Description', 
            filter: 'agSetColumnFilter',
            minWidth: 180
        },
        { 
            field: 'quantity', 
            headerName: `Quantity${effectiveDisplayUnit ? ` (${effectiveDisplayUnit})` : ''}`, 
            filter: 'agNumberColumnFilter',
            type: 'numericColumn',
            headerClass: 'ag-header-cell-left ag-header-numeric-left',
            cellStyle: { textAlign: 'right' },
            valueFormatter: (params) => params.value != null ? params.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
            minWidth: 120
        },
        {
            field: 'originalQuantity',
            headerName: 'Original Quantity',
            filter: 'agNumberColumnFilter',
            type: 'numericColumn',
            headerClass: 'ag-header-cell-left ag-header-numeric-left',
            cellStyle: { textAlign: 'right' },
            valueFormatter: (params) => params.value != null ? params.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
            minWidth: 140
        },
        { 
            field: 'baseUnitofMeasure', 
            headerName: 'Original Unit', 
            filter: 'agSetColumnFilter',
            minWidth: 100
        },
        { 
            field: 'amountInLocation', 
            headerName: 'Amount', 
            filter: 'agNumberColumnFilter',
            type: 'numericColumn',
            headerClass: 'ag-header-cell-left ag-header-numeric-left',
            cellStyle: { textAlign: 'right' },
            valueFormatter: (params) => params.value != null ? params.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
            minWidth: 120
        },
        { 
            field: 'movementType', 
            headerName: 'Movement Type', 
            filter: 'agSetColumnFilter',
            minWidth: 110
        },
        { 
            field: 'movementTypeText', 
            headerName: 'Movement Text', 
            filter: 'agSetColumnFilter',
            minWidth: 150
        },
        { 
            field: 'storageLocation', 
            headerName: 'Storage Location', 
            filter: 'agSetColumnFilter',
            minWidth: 120
        },
        { 
            field: 'order', 
            headerName: 'Order', 
            filter: 'agSetColumnFilter',
            minWidth: 120
        },
        { 
            field: 'purchaseOrder', 
            headerName: 'Purchase Order', 
            filter: 'agSetColumnFilter',
            minWidth: 120
        }
    ], [effectiveDisplayUnit]);

    const gridOptions = useMemo(() => ({
        defaultColDef: defaultColDef,
        onFilterChanged: handleFilterChanged,
        onGridReady: (params: any) => {
            gridRef.current = params;
            handleFilterChanged();
        },
    }), [handleFilterChanged, defaultColDef]);

    if (allocations.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body1" color="textSecondary">
                    No allocations available to display deliveries.
                </Typography>
            </Box>
        );
    }

    const hasActiveFilters = filteredRowCount !== totalRowCount;

    const SummaryBox = () => (
        <Paper
            elevation={1}
            sx={{
                p: 1.5,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                flexDirection: 'column',
                backgroundColor: hasActiveFilters ? '#e3f2fd' : '#f5f5f5',
                gap: 0.5
            }}
        >
            <Typography variant="body2">
                <strong>Total Quantity{effectiveDisplayUnit ? ` (${effectiveDisplayUnit})` : ''}:</strong> {filteredQuantitySum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography variant="body2">
                <strong>Total Amount:</strong> {filteredAmountSum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
        </Paper>
    );

    const handleExportToExcel = useCallback(() => {
        const api = gridRef.current?.api;
        if (!api) return;

        const visibleColumns = api.getAllDisplayedColumns();
        const headers = visibleColumns.map((col: any) => col.getColDef().headerName ?? col.getColId());

        const rows: any[][] = [];
        api.forEachNodeAfterFilterAndSort((node: any) => {
            if (!node.data) return;
            const row = visibleColumns.map((col: any) => {
                const colDef = col.getColDef();
                const field = colDef.field;
                let value: any = field ? node.data[field] : undefined;
                if (colDef.valueGetter && typeof colDef.valueGetter === 'function') {
                    value = colDef.valueGetter({ data: node.data, node, colDef, column: col, api } as any);
                }
                if (value instanceof Date) {
                    return dayjs(value).format('MM-DD-YYYY');
                }
                return value ?? '';
            });
            rows.push(row);
        });

        const aoa = [headers, ...rows];
        const sheet = XLSX.utils.aoa_to_sheet(aoa);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, sheet, 'Deliveries');
        const filename = `Deliveries_${dayjs().format('YYYYMMDD')}.xlsx`;
        XLSX.writeFile(workbook, filename);
    }, []);

    return (
        <Box sx={{ width: '100%' }} className="deliveries-data-grid">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Deliveries Report</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportToExcel}
                        disabled={convertedDeliveries.length === 0}
                    >
                        Download Excel
                    </Button>
                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel id="allocation-select-label">Supplier</InputLabel>
                        <Select
                            labelId="allocation-select-label"
                            id="allocation-select"
                            value={selectedAllocationId}
                            label="Supplier"
                            onChange={handleAllocationChange}
                        >
                            <MenuItem value="all">All Suppliers</MenuItem>
                            {allocations.map(alloc => (
                                <MenuItem key={alloc.allocationGroupId} value={alloc.allocationGroupId}>
                                    {alloc.counterparty.counterpartyName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {convertedDeliveries.length === 0 ? (
                <Typography variant="body1" color="textSecondary">
                    No deliveries found for the selected supplier.
                </Typography>
            ) : (
                <>
                    {/* Top Summary */}
                    <SummaryBox />

                    <Box sx={{ mt: 1, mb: 1 }}>
                        <StoneXGrid
                            ref={gridRef}
                            rows={convertedDeliveries}
                            columns={columns}
                            gridOptions={gridOptions}
                            getRowId={(params) => params.data.deliveryId}
                        />
                    </Box>

                    {/* Bottom Summary */}
                    <SummaryBox />
                </>
            )}
        </Box>
    );
};

export default DeliveriesData;